import { useState } from "react";
import { styled } from "styled-components";
import { createLinkProps } from "../../hooks/useLinkProps.js";
import { MultiLevelHeaderMenuItem, ThemeSettings } from "../../types/theme.js";
import { chevron } from "../../icons/index.js";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { createLabel } from "../../util/createLabel.js";
import { Button } from "../../internal-components/Button.js";
interface Props {
  themeSettings: ThemeSettings;
}

export const Navigation: React.FC<Props> = ({ themeSettings }) => {
  const [openSubMenu, setOpenSubMenu] = useState<number | null>(null);

  const menu = themeSettings.headerMenu || [];
  const primaryAction = themeSettings.headerPrimaryAction;
  const hasPrimaryAction = !!primaryAction;
  const level = 0;

  return (
    <NavigationWrapper className="desktop">
      <Constrainer>
        <MenuContainer>
          <Menu className={`top-level-menu desktop`}>
            {menu.map((menuItem, menuItemIndex) => {
              const hasSubMenu =
                "subMenu" in menuItem &&
                menuItem.subMenu &&
                menuItem.subMenu.length > 0;

              return (
                <li
                  key={menuItemIndex}
                  className={`${openSubMenu === menuItemIndex ? "active" : ""}`}
                >
                  <a
                    className="b2"
                    onClick={(e) => {
                      if ("label" in menuItem) {
                        e.preventDefault();
                        setOpenSubMenu(
                          openSubMenu === menuItemIndex ? null : menuItemIndex
                        );
                      }
                    }}
                    {...(menuItem.link
                      ? { ...createLinkProps(menuItem.link) }
                      : { href: "#" })}
                  >
                    {createLabel(menuItem)} {hasSubMenu && <>{chevron}</>}
                  </a>

                  {"subMenu" in menuItem &&
                    menuItem.subMenu &&
                    openSubMenu === menuItemIndex && (
                      <SubMenu
                        menuItem={menuItem}
                        level={level}
                        hasPrimaryAction={hasPrimaryAction}
                      />
                    )}
                </li>
              );
            })}
          </Menu>
        </MenuContainer>
        {primaryAction && (
          <PrimaryAction>
            <Button as="a" {...createLinkProps(primaryAction)}>
              {primaryAction.title}
            </Button>
          </PrimaryAction>
        )}
      </Constrainer>
    </NavigationWrapper>
  );
};

interface SubMenuProps {
  menuItem: MultiLevelHeaderMenuItem;
  hasPrimaryAction: boolean;
  level: number;
}

const SubMenu: React.FC<SubMenuProps> = ({
  menuItem,
  hasPrimaryAction,
  level,
}) => {
  const menuClassName = `sub-menu-${level + 1} ${
    hasPrimaryAction ? "high" : "low"
  }`;

  return (
    <Menu className={menuClassName}>
      {(menuItem.subMenu || []).map((subMenuItem, subMenuItemIndex) => {
        const hasSubMenu =
          subMenuItem.subMenu && subMenuItem.subMenu.length > 0;

        return (
          <li
            key={subMenuItemIndex}
            className={`${
              hasSubMenu ? "has-sub-menu" : ""
            } index-${subMenuItemIndex}`}
          >
            <a
              className="b2"
              {...(subMenuItem.link
                ? { ...createLinkProps(subMenuItem.link) }
                : { href: "#" })}
            >
              {createLabel(subMenuItem)} {hasSubMenu && <>{chevron}</>}
            </a>

            {subMenuItem.subMenu && (
              <div className="sub-menu-container">
                <SubMenu
                  menuItem={subMenuItem}
                  level={level + 1}
                  hasPrimaryAction={hasPrimaryAction}
                />
              </div>
            )}
          </li>
        );
      })}
    </Menu>
  );
};

export const Constrainer = styled.div`
  margin: 0 auto;
  max-width: 1100px;
  display: flex;
  padding: 0 ${(props) => props.theme.spacing.xs};
  align-items: center;
  position: relative;
  width: 100%;
  justify-content: flex-start;
`;

export const MenuContainer = styled.div``;

export const PrimaryAction = styled.div`
  margin-left: auto;
  padding-left: ${(props) => props.theme.spacing.m};
  flex-shrink: 0;
`;

export const NavigationWrapper = styled.nav`
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 9999;

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${(props) => props.theme.spacing.m} 0;
  }

  ul {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    z-index: 10;
    list-style: none;
    padding; 0;
    margin: 0;
    position: relative;

    a {
      display: block;
      color: inherit;
      text-decoration: none;
      padding: 0 ${(props) => props.theme.spacing.m};
    }
  }

  .top-level-menu > li:first-child > a {
    padding-left: 0;
  }

  .button:not(:first-child) {
    margin-left: ${(props) => props.theme.spacing.m};
  }
`;

const Menu = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  z-index: 10;

  li.path-active {
    color: red;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    &.top-level-menu {
      position: relative;

      > li {
        position: relative; /* Important: Anchors submenus */

        &.active > a {
          > svg {
            transform: rotate(-90deg);
          }
        }

        > a {
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: inherit;
          text-decoration: none;
          z-index: 11;

          > svg {
            width: 16px;
            height: 16px;
            transform: rotate(90deg);
            margin-left: ${(props) => props.theme.spacing.xs};
          }
        }
      }
    }

    /* All submenus */
    &.sub-menu-1,
    &.sub-menu-2,
    &.sub-menu-3,
    &.sub-menu-4 {
      border: 1px solid rgba(0, 0, 0, 0.12);
      background-color: #fff;
      color: var(--swatch2);
      position: absolute;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      width: max-content; /* Ensures submenu width matches content */
      min-width: 150px;

      > li {
        width: 100%;
        position: relative; /* Ensures nested submenus position correctly */

        > a {
          height: 50px;
          display: flex;
          align-items: center;
          width: 100%;
          box-sizing: border-box;

          &:hover,
          &:focus {
            background-color: var(--swatch4);
            color: var(--swatch2);
          }

          > svg {
            transform: rotate(0deg);
            width: 16px;
            height: 16px;
            margin-left: ${(props) => props.theme.spacing.xs};
          }
        }
      }
    }

    /* First-level submenu: appears BELOW */
    &.sub-menu-1 {
      top: calc(100% + 28px);
      left: 0;
      display: flex;
    }

    &.sub-menu-1.low {
      top: calc(100% + 26px);
    }

    &.sub-menu-2,
    &.sub-menu-3,
    &.sub-menu-4 {
      top: -1px;
      left: 100%;
    }

    /* Keep background color when sub-level menu is open */
    .sub-menu-1 li.has-sub-menu:hover > a,
    .sub-menu-2 li.has-sub-menu:hover > a,
    .sub-menu-3 li.has-sub-menu:hover > a {
      background-color: var(--swatch4); /* Same as hover */
      color: var(--swatch2);
    }

    .has-sub-menu {
      position: relative;
    }

    .has-sub-menu:hover > .sub-menu-container,
    .has-sub-menu:focus-within > .sub-menu-container {
      display: block;
    }

    .sub-menu-container {
      display: none;
      position: absolute;
      top: 0;
      left: 100%; /* Positions to the right of the parent item */
    }
  }
`;
